import { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { Stepper } from 'primereact/stepper';
import { StepperPanel } from 'primereact/stepperpanel';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import { authApi } from '../../api/auth';
import { TwoFactorSetupResponse } from '../../types';
import { BackupCodesDisplay } from './BackupCodesDisplay';

export interface TwoFactorSetupRef {
  activeStep: number;
  isVerifying: boolean;
  canVerify: boolean;
  goToStep: (step: number) => void;
  verify: () => Promise<void>;
}

interface TwoFactorSetupProps {
  onStepChange?: (step: number) => void;
  onStateChange?: (state: { isVerifying: boolean; canVerify: boolean }) => void;
}

export const TwoFactorSetup = forwardRef<TwoFactorSetupRef, TwoFactorSetupProps>(({
  onStepChange,
  onStateChange,
}, ref) => {
  const toast = useRef<Toast>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [setupData, setSetupData] = useState<TwoFactorSetupResponse | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [showManualKey, setShowManualKey] = useState(false);

  const canVerify = verificationCode.length === 6;

  useEffect(() => {
    initializeSetup();
  }, []);

  useEffect(() => {
    onStepChange?.(activeStep);
  }, [activeStep, onStepChange]);

  useEffect(() => {
    onStateChange?.({ isVerifying, canVerify });
  }, [isVerifying, canVerify, onStateChange]);

  useImperativeHandle(ref, () => ({
    activeStep,
    isVerifying,
    canVerify,
    goToStep: (step: number) => setActiveStep(step),
    verify: handleVerify,
  }));

  const initializeSetup = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await authApi.setup2FA();
      setSetupData(response);
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to initialize 2FA setup';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async () => {
    if (verificationCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setIsVerifying(true);
    setError(null);
    try {
      const response = await authApi.verifySetup2FA(verificationCode);
      setBackupCodes(response.backup_codes);
      setActiveStep(2);
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: '2FA has been enabled',
        life: 3000,
      });
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Invalid verification code';
      setError(errorMessage);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCodeChange = (value: string) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(cleaned);
    setError(null);
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <ProgressSpinner />
        <p>Setting up two-factor authentication...</p>
      </div>
    );
  }

  return (
    <>
      <Toast ref={toast} />

      <Stepper activeStep={activeStep} linear>
        <StepperPanel header="Scan QR Code">
          <p>
            Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
          </p>

          {setupData && (
            <>
              <div className="two-factor-qr">
                <img
                  src={`data:image/png;base64,${setupData.qr_code}`}
                  alt="2FA QR Code"
                />
              </div>

              <Button
                label={showManualKey ? 'Hide manual key' : "Can't scan? Enter key manually"}
                link
                onClick={() => setShowManualKey(!showManualKey)}
              />
              {showManualKey && (
                <div className="two-factor-secret">
                  <code>{setupData.secret}</code>
                  <Button
                    icon="ms-out ms-out-content-copy"
                    text
                    size="small"
                    tooltip="Copy key"
                    onClick={() => {
                      navigator.clipboard.writeText(setupData.secret);
                      toast.current?.show({
                        severity: 'success',
                        summary: 'Copied',
                        detail: 'Key copied to clipboard',
                        life: 2000,
                      });
                    }}
                  />
                </div>
              )}
            </>
          )}
        </StepperPanel>

        <StepperPanel header="Verify">
          <p>Enter the 6-digit code from your authenticator app to verify setup.</p>

          {error && <Message severity="error" text={error} />}

          <div className="two-factor-code-input">
            <InputText
              value={verificationCode}
              onChange={(e) => handleCodeChange(e.target.value)}
              placeholder="000000"
              maxLength={6}
              disabled={isVerifying}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && verificationCode.length === 6) {
                  handleVerify();
                }
              }}
            />
          </div>
        </StepperPanel>

        <StepperPanel header="Backup Codes">
          <BackupCodesDisplay codes={backupCodes} />
        </StepperPanel>
      </Stepper>
    </>
  );
});

TwoFactorSetup.displayName = 'TwoFactorSetup';

export default TwoFactorSetup;
